import { useNavigate } from "react-router-dom";
import { LifeBuoy, BookOpen, MessageSquare, ShieldCheck, ArrowRight, Library } from "lucide-react";
import { Button } from "@/components/ui/button";
import PageShell from "@/components/premium/PageShell";

type ResourceCard = {
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  description: string;
  cta: string;
  to: string;
  accent: string;
};

const RESOURCES: ResourceCard[] = [
  {
    icon: LifeBuoy,
    title: "Help & Support",
    description:
      "Get help with your account, billing, lessons, parent dashboards, child access, and technical issues.",
    cta: "Open Support",
    to: "/support",
    accent: "from-primary/20 to-primary/5 border-primary/40",
  },
  {
    icon: BookOpen,
    title: "Financial Vocabulary",
    description:
      "Quick, plain-language definitions for the terms used across your lessons — APR, ROI, assets, liabilities, and more.",
    cta: "Browse Vocabulary",
    to: "/financial-vocabulary",
    accent: "from-accent/20 to-accent/5 border-accent/40",
  },
  {
    icon: MessageSquare,
    title: "Send Feedback",
    description:
      "Share ideas, report issues, or suggest improvements. Your feedback directly shapes HeirWise.",
    cta: "Open Feedback",
    to: "/feedback",
    accent: "from-secondary/20 to-secondary/5 border-secondary/40",
  },
  {
    icon: ShieldCheck,
    title: "Account Security",
    description:
      "Manage your password, sign-in security, and account access from one place.",
    cta: "Open Security",
    to: "/settings/security",
    accent: "from-primary/20 to-primary/5 border-primary/40",
  },
];

const ResourcesPage = () => {
  const navigate = useNavigate();

  return (
    <PageShell className="space-y-8">
      <h1 className="sr-only">Resources</h1>
        <section className="relative overflow-hidden rounded-3xl border border-border/60 bg-gradient-to-br from-black via-[hsl(260_30%_7%)] to-[hsl(225_35%_6%)] p-8 sm:p-10">
          <div className="absolute -top-20 -right-20 h-60 w-60 rounded-full bg-primary/10 blur-3xl" />
          <div className="relative space-y-3 max-w-2xl">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-primary/30 bg-primary/10 text-xs font-heading text-primary uppercase tracking-widest">
              <Library className="h-3.5 w-3.5" /> Resource Hub
            </div>
            <h2 className="font-heading font-extrabold text-2xl sm:text-4xl text-foreground">Everything you need, in one place</h2>
            <p className="text-sm sm:text-base text-foreground/80">
              Support, vocabulary, feedback, and account tools to help you get the most from HeirWise.
            </p>
          </div>
        </section>

        <section className="grid sm:grid-cols-2 gap-4">
          {RESOURCES.map((r) => {
            const Icon = r.icon;
            return (
              <button
                key={r.title}
                onClick={() => navigate(r.to)}
                className={`group text-left rounded-2xl border bg-gradient-to-br ${r.accent} backdrop-blur-xl p-6 transition-all hover:scale-[1.01] hover:shadow-[0_10px_40px_hsl(var(--primary)/0.2)]`}
              >
                <div className="flex items-start gap-4">
                  <div className="shrink-0 w-11 h-11 rounded-xl border border-border/60 bg-card/70 flex items-center justify-center">
                    <Icon className="h-5 w-5 text-foreground" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-heading font-bold text-lg text-foreground">{r.title}</h3>
                    <p className="text-sm text-foreground/75 mt-1.5 leading-relaxed">{r.description}</p>
                    <div className="mt-4 inline-flex items-center gap-1.5 text-sm font-medium text-primary group-hover:gap-2 transition-all">
                      {r.cta} <ArrowRight className="h-4 w-4" />
                    </div>
                  </div>
                </div>
              </button>
            );
          })}
        </section>
    </PageShell>
  );
};

export default ResourcesPage;